import { useEffect, useMemo, useState } from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import { RowSelectionState } from '@tanstack/react-table';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { subsetFormSchema, SubsetFormData } from './subset-form-schema';
import { SubsetSuggestionStatus } from '../../../../../../../../backend/src/case/subset/subset-suggestion.entity';
import { cn } from '../../../../../lib/utils';
import { TrackingEventsV2 } from '@/analytics/eventsV2';
import { trackCustomEvent } from '@/analytics/Mixpanel';
import { useCreateDocSubset } from '@/api/mutations/useCreateDocSubset';
import { useUpdateSubset } from '@/api/mutations/useUpdateSubset';
import { useUpdateSuggestionStatus } from '@/api/mutations/useUpdateSuggestionStatus';
import { useGetMatterDocs } from '@/api/queries/useGetMatterDocs';
import { useGetFilterOptions } from '@/api/queries/useGetMetadataFilterOptions';
import { useGetProcessingDocs } from '@/api/queries/useGetProcessingDocs';
import { useGetSubsets } from '@/api/queries/useGetSubsets';
import { ConfirmationModal } from '@/new/components/dialogs/confirmation-dialog';
import { Icon } from '@/new/components/icon';
import { PageContainer } from '@/new/components/page-container';
import { PageTitle } from '@/new/components/page-title';
import { Button } from '@/new/components/shadcn/button';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/new/components/shadcn/form';
import { Input } from '@/new/components/shadcn/input';
import { Popover, PopoverTrigger, PopoverContent } from '@/new/components/shadcn/popover';
import { Textarea } from '@/new/components/shadcn/textarea';
import { DocPickerTable } from '@/new/pages/matters/:matterId/subsets/new/components/doc-picker-table';

interface SubsetFormProps {
  mode: 'create' | 'edit';
  matterId: string;
  subsetId?: string;
  initialData?: {
    name: string;
    legalIssues: string;
    selectedDocIds: string[];
  };
  suggestionId?: string;
  isLoadingInitialData?: boolean;
  onSuccess?: (subsetId: string) => void;
  matterIssues?: string;
}

export function SubsetForm({
  mode,
  matterId,
  subsetId,
  initialData,
  suggestionId,
  isLoadingInitialData,
  onSuccess,
  matterIssues,
}: SubsetFormProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data: docs, isLoading: isLoadingDocs } = useGetMatterDocs(matterId);
  const { data: subsets } = useGetSubsets(matterId);
  const { data: processingDocsData } = useGetProcessingDocs(matterId);
  const createSubset = useCreateDocSubset();
  const updateSubset = useUpdateSubset();
  const updateSuggestionStatus = useUpdateSuggestionStatus();

  const form = useForm<SubsetFormData>({
    resolver: zodResolver(subsetFormSchema),
    mode: 'onTouched',
    defaultValues: {
      name: initialData?.name ?? '',
      legalIssues: initialData?.legalIssues ?? '',
    },
  });

  // Reset form when initial data changes (e.g., when data loads)
  useEffect(() => {
    if (initialData) {
      form.reset({
        name: initialData.name,
        legalIssues: initialData.legalIssues,
      });
    }
  }, [initialData]);

  // Create a Set of processing document IDs for efficient lookup
  const processingDocIds = useMemo(() => new Set(processingDocsData?.docIds ?? []), [processingDocsData?.docIds]);

  // Find the all docs case ID for NL filtering since for now we need that to build NL filters
  const allDocumentsCase = useMemo(() => {
    return subsets?.find((subset) => subset.isAllDocuments);
  }, [subsets]);

  // Fetch filter options for dynamic columns (Relativity and Wexler fields)
  const { data: filterOptions } = useGetFilterOptions({ matterId });

  // Confirmation dialog state for edit mode
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);

  // Watch form values for change detection
  const watchedName = form.watch('name');
  const watchedLegalIssues = form.watch('legalIssues');

  // Check if values have changed from initial
  const nameHasChanged = useMemo(() => watchedName !== (initialData?.name ?? ''), [watchedName, initialData?.name]);
  const issuesHaveChanged = useMemo(
    () => watchedLegalIssues !== (initialData?.legalIssues ?? ''),
    [watchedLegalIssues, initialData?.legalIssues],
  );

  // Initialize row selection from initial data
  const [rowSelection, setRowSelection] = useState<RowSelectionState>(() => {
    if (initialData?.selectedDocIds) {
      return initialData.selectedDocIds.reduce((acc, docId) => {
        acc[docId] = true;
        return acc;
      }, {} as RowSelectionState);
    }
    return {};
  });

  // Update row selection when initial data changes
  useEffect(() => {
    if (initialData?.selectedDocIds) {
      setRowSelection(
        initialData.selectedDocIds.reduce((acc, docId) => {
          acc[docId] = true;
          return acc;
        }, {} as RowSelectionState),
      );
    }
  }, [initialData?.selectedDocIds]);

  // Track initial document IDs for edit mode comparison
  const initialDocIds = useMemo(() => initialData?.selectedDocIds ?? [], [initialData]);

  const selectedCount = useMemo(() => Object.keys(rowSelection).length, [rowSelection]);
  const selectedDocIds = useMemo(() => Object.keys(rowSelection), [rowSelection]);

  // Calculate document changes for edit mode
  const documentChanges = useMemo(() => {
    if (mode !== 'edit') return { added: 0, removed: 0 };

    const addedDocs = selectedDocIds.filter((id) => !initialDocIds.includes(id));
    const removedDocs = initialDocIds.filter((id) => !selectedDocIds.includes(id));

    return {
      added: addedDocs.length,
      removed: removedDocs.length,
    };
  }, [mode, selectedDocIds, initialDocIds]);

  // Check if any changes have been made in edit mode
  const hasChanges = useMemo(() => {
    if (mode !== 'edit' || !initialData) return true;

    const nameChanged = watchedName !== initialData.name;
    const issuesChanged = watchedLegalIssues !== initialData.legalIssues;
    const docsChanged = documentChanges.added > 0 || documentChanges.removed > 0;

    return nameChanged || issuesChanged || docsChanged;
  }, [mode, initialData, watchedName, watchedLegalIssues, documentChanges]);

  const handleButtonClick = form.handleSubmit(() => {
    // Don't proceed if no docs selected
    if (selectedDocIds.length === 0) return;

    // Show confirmation dialog for edit mode, otherwise submit directly
    if (mode === 'edit') {
      setShowConfirmDialog(true);
    } else {
      handleSubmit();
    }
  });

  const handleSubmit = async () => {
    const formValues = form.getValues();

    try {
      let result: { id: string } | undefined = undefined;

      if (mode === 'create') {
        result = await createSubset.mutateAsync({
          matterId: matterId,
          body: {
            title: formValues.name,
            legalIssues: formValues.legalIssues,
            docIds: selectedDocIds,
          },
        });

        // Track subset creation
        if (result) {
          trackCustomEvent(TrackingEventsV2.CreateSubset, {
            matterId,
            subsetId: result.id,
            documentCount: selectedDocIds.length,
          });
        }
      } else {
        result = await updateSubset.mutateAsync({
          matterId,
          subsetId: subsetId!,
          body: {
            name: formValues.name,
            legalIssues: formValues.legalIssues,
            docIds: selectedDocIds,
          },
        });

        // Track subset update
        if (result) {
          trackCustomEvent(TrackingEventsV2.UpdateSubset, {
            matterId,
            subsetId: result.id,
            documentCount: selectedDocIds.length,
            docsAdded: documentChanges.added,
            docsRemoved: documentChanges.removed,
          });
        }
      }

      // Mark suggestion as accepted if this was created from a suggestion
      if (result && mode === 'create' && suggestionId) {
        await updateSuggestionStatus.mutateAsync({
          suggestionId,
          status: 'accepted' as SubsetSuggestionStatus.Accepted, // enum can't be used directly on FE - don't use enums in future
        });
      }

      if (result && onSuccess) {
        onSuccess(result.id);
      } else if (result) {
        navigate(`/matters/${matterId}/subsets/${result.id}`);
      }

      // Close the dialog if it was open
      setShowConfirmDialog(false);
    } catch (error) {
      console.error(`Failed to ${mode} subset:`, error);
    }
  };

  const isLoading = mode === 'create' ? createSubset.isLoading : updateSubset.isLoading;
  const buttonText = mode === 'create' ? 'Create Subset' : 'Update Subset';
  const loadingText = mode === 'create' ? 'Creating...' : 'Updating...';

  return (
    <PageContainer className="flex h-full flex-col overflow-hidden px-0">
      <div className="flex grow overflow-hidden">
        <section className="flex flex-col">
          <div className="flex items-center gap-4 p-7 pr-5">
            <PageTitle text={mode === 'create' ? 'Create Subset' : 'Edit Subset'} iconClass="lucide--group" />

            <Popover>
              <PopoverTrigger>
                <Icon className="lucide--info mt-1.25 text-base text-gray-400 hover:text-gray-600" />
              </PopoverTrigger>

              <PopoverContent side="right" sideOffset={14} className="mt-4 text-gray-800">
                <div className="px-1">
                  Subsets allow you to easily {t('analyse')} facts from a select group of documents.
                </div>
              </PopoverContent>
            </Popover>
          </div>

          <Form {...form}>
            <form
              className="m-2 mt-0 flex w-sm grow flex-col justify-between overflow-y-hidden rounded-md border bg-gray-50 shadow-lg/2"
              data-tour="subset-form"
              onSubmit={handleButtonClick}
            >
              <div className="flex grow flex-col justify-between overflow-y-auto">
                <div className="flex flex-col gap-2 px-5 py-2">
                  <FormField
                    control={form.control}
                    name="name"
                    render={({ field }) => (
                      <FormItem>
                        <div className="my-3 flex items-center gap-1">
                          <FormLabel className="mb-0 flex items-center gap-2 px-1">
                            <p className="text-gray-700">
                              Subset Name <span className="text-teal-500">*</span>
                            </p>
                          </FormLabel>

                          {nameHasChanged && (
                            <button
                              type="button"
                              onClick={() => {
                                form.setValue('name', initialData?.name ?? '', { shouldValidate: true });
                              }}
                              className="animate-in fade-in w-fit text-gray-400 duration-100 hover:text-gray-600"
                              title="Undo changes"
                            >
                              <Icon className="lucide--rotate-ccw text-sm" />
                            </button>
                          )}
                        </div>

                        <FormControl>
                          <Input type="text" placeholder="John Doe's Emails" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="legalIssues"
                    render={({ field }) => (
                      <FormItem>
                        <div className="my-3 flex items-center gap-1">
                          <FormLabel className="mb-0 flex items-center gap-2 px-1">
                            <p className="text-gray-700">
                              Subset Issues <span className="text-teal-500">*</span>
                            </p>
                          </FormLabel>

                          {issuesHaveChanged && (
                            <button
                              type="button"
                              onClick={() => {
                                form.setValue('legalIssues', initialData?.legalIssues ?? '', { shouldValidate: true });
                              }}
                              className="animate-in fade-in w-fit text-gray-400 duration-100 hover:text-gray-600"
                              title="Undo changes"
                            >
                              <Icon className="lucide--rotate-ccw text-sm" />
                            </button>
                          )}
                        </div>
                        <p className="my-2 mb-4 px-1 text-gray-500">
                          Subset-specific issues are used to determine the relevance classification of facts.
                        </p>
                        <FormControl>
                          <Textarea placeholder="John Doe's awareness of fraudulent activity" rows={14} {...field} />
                        </FormControl>
                        <FormMessage />
                        {matterIssues && (
                          <Button
                            type="button"
                            variant="default"
                            size="sm"
                            className="mt-3"
                            onClick={() => {
                              form.setValue('legalIssues', matterIssues, { shouldValidate: true });
                              trackCustomEvent(TrackingEventsV2.CopyMatterIssuesToSubset, { matterId });
                            }}
                          >
                            <Icon className="lucide--copy" />
                            Copy Matter Issues
                          </Button>
                        )}
                      </FormItem>
                    )}
                  />
                </div>

                {mode === 'create' && (
                  <p className="flex items-center gap-3 p-5 pr-6 text-balance text-gray-500">
                    <Icon className="lucide--info mb-0.75 text-gray-400" />
                    You can always add or remove documents from a Subset after creation.
                  </p>
                )}
              </div>

              <div className="rounded-b-md border-t bg-white p-4">
                {selectedCount > 20000 && (
                  <div className="mb-3 flex items-start gap-3 rounded-md border border-amber-300 bg-amber-50 p-4 text-amber-700">
                    <Icon className="lucide--triangle-alert mt-1" />
                    We're currently working on stability improvements for large subsets. You may experience issues when
                    trying to select more than 20,000 documents.
                  </div>
                )}

                <div className="flex w-full items-center justify-between">
                  {mode === 'edit' && (documentChanges.added > 0 || documentChanges.removed > 0) ? (
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <Icon className="lucide--file h-3.5 w-3.5 text-gray-400" />
                      <span>
                        {documentChanges.added > 0 && (
                          <span className="text-green-600">+{documentChanges.added} added</span>
                        )}
                        {documentChanges.added > 0 && documentChanges.removed > 0 && <span>, </span>}
                        {documentChanges.removed > 0 && (
                          <span className="text-red-600">-{documentChanges.removed} removed</span>
                        )}
                      </span>
                    </div>
                  ) : (
                    <div />
                  )}

                  <Button
                    type="submit"
                    variant="primary"
                    className="w-fit"
                    disabled={!selectedCount || isLoading || !hasChanges}
                  >
                    <Icon className={cn(isLoading ? 'lucide--loader-circle animate-spin' : 'lucide--group')} />
                    <span>
                      {isLoading
                        ? loadingText
                        : mode === 'create'
                          ? `${buttonText} with ${selectedCount} Document${selectedCount === 1 ? '' : 's'}`
                          : buttonText}
                    </span>
                  </Button>
                </div>
              </div>
            </form>
          </Form>
        </section>

        <DocPickerTable
          docs={docs}
          rowSelection={rowSelection}
          onRowSelectionChange={setRowSelection}
          allDocumentsCaseId={allDocumentsCase?.id ?? ''}
          matterId={matterId}
          mode={mode}
          initialDocIds={mode === 'edit' ? initialDocIds : undefined}
          defaultShowSelected={!!suggestionId}
          isLoadingInitialSelection={isLoadingInitialData || isLoadingDocs}
          processingDocIds={processingDocIds}
          relativityFields={filterOptions?.relativityFields?.fields}
          relativityDistinctValues={filterOptions?.relativityFields?.availableOptions}
          wexlerFields={filterOptions?.wexlerFields?.fields}
          wexlerDistinctValues={filterOptions?.wexlerFields?.availableOptions}
        />
      </div>

      <ConfirmationModal
        isOpen={showConfirmDialog}
        onOpenChange={setShowConfirmDialog}
        title="Update Subset"
        onConfirm={handleSubmit}
        isLoading={isLoading}
      >
        <div className="space-y-5">
          <p className="text-gray-700">You are about to update this subset with the following changes:</p>

          <div className="ml-4 space-y-2">
            {nameHasChanged && (
              <div className="flex items-center gap-3 text-sm">
                <Icon className="lucide--pencil" />
                <span className="text-gray-700">
                  Name changed to: <span className="font-medium">{watchedName}</span>
                </span>
              </div>
            )}

            {issuesHaveChanged && (
              <div className="flex items-center gap-3 text-sm">
                <Icon className="lucide--pencil" />

                <span className="text-gray-700">Legal issues updated</span>
              </div>
            )}

            {documentChanges.added > 0 && (
              <div className="flex items-center gap-3 text-sm">
                <Icon className="lucide--file-plus" />
                <span>
                  <span className="font-semibold">{documentChanges.added}</span> document
                  {documentChanges.added === 1 ? '' : 's'} added
                </span>
              </div>
            )}

            {documentChanges.removed > 0 && (
              <div className="flex items-center gap-3 text-sm">
                <Icon className="lucide--file-minus" />
                <span>
                  <span className="font-semibold">{documentChanges.removed}</span> document
                  {documentChanges.removed === 1 ? '' : 's'} removed
                </span>
              </div>
            )}
          </div>
        </div>
      </ConfirmationModal>
    </PageContainer>
  );
}
